import type * as React from 'react'
import type { JSX } from 'react'

import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'

import { ChannelIcon, PlusIcon, UnstyledButton } from '@buffer-mono/popcorn'

import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { getChannelName } from '~publish/helpers/channels'
import { format } from '~publish/helpers/temporal'

import styles from './CalendarSlotButton.module.css'

export type CalendarSlotButtonVariant = 'default' | 'month' | 'week'

type CalendarSlotButtonProps = Omit<
  React.ComponentProps<typeof UnstyledButton>,
  'children'
> & {
  timestamp: number
  timezone: string
  channelId: string | null
  channelName?: string
  channelService?: Service
  isDraggingOver?: boolean
  is24HourFormat?: boolean
  variant?: CalendarSlotButtonVariant
}

export function CalendarSlotButton({
  timestamp,
  timezone,
  channelId,
  channelName,
  channelService,
  isDraggingOver,
  className,
  is24HourFormat = false,
  variant = 'default',
  ...props
}: CalendarSlotButtonProps): JSX.Element {
  const time =
    Temporal.Instant.fromEpochMilliseconds(timestamp).toZonedDateTimeISO(
      timezone,
    )
  const timeString = is24HourFormat
    ? format(time, 'HH:mm')
    : format(time, 'h:mm a')
  const channelLabel = channelService
    ? channelId && channelName
      ? `${channelName} on ${getChannelName(channelService)}`
      : getChannelName(channelService)
    : undefined
  const isCompact = variant !== 'default'

  return (
    <UnstyledButton
      className={clsx(
        styles.slot,
        {
          [styles.monthSlot]: variant === 'month',
          [styles.weekSlot]: variant === 'week',
        },
        className,
        {
          [styles.withChannel]: channelService !== undefined,
          [styles.isDraggingOver]: isDraggingOver,
        },
      )}
      aria-label={
        channelLabel
          ? `Add a post to ${channelLabel} at ${timeString}`
          : isCompact
            ? `Add a post at ${timeString}`
            : undefined
      }
      title={variant === 'week' ? timeString : undefined}
      {...props}
    >
      {channelService ? (
        <ChannelIcon
          type={channelService}
          color="subtle"
          size={isCompact ? 12 : 16}
          aria-hidden
        />
      ) : (
        variant === 'month' && <PlusIcon size="xsmall" color="subtle" />
      )}
      {variant !== 'month' && <span className={styles.time}>{timeString}</span>}
      {variant === 'default' && (
        <span className={styles.hoverText}>Add a Post</span>
      )}
    </UnstyledButton>
  )
}
